import { DateUtils, Model, NeoModel } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import type { IInvestmentHostApiClient } from '../../../ApiClients/IInvestmentHostApiClient';
import type { IInvestmentHost } from '../../../Models/Investment/IInvestmentHost';
import { AppService, DomainTypes, Types } from '../../../DomainTypes';
import DateCycle from '../../../Models/Dividends/DateCycle';
import UtilisationCriteria from '../../../Models/IssuerInvestments/Lookups/UtilisationCriteria';
import UtilisationLookup from '../../../Models/IssuerInvestments/Lookups/UtilisationLookup';
import ShareTransactionCommand, { TransactionType } from '../../../Models/Transactions/Commands/ShareTransactionCommand';
import DateCyclesVM from '../../InvestmentSetup/DateCyclesVM';
import ShareTransactionLookup from '../../../Models/Transactions/Lookups/ShareTransactionLookup';
import CreateTransactionResult from '../../../Models/Transactions/Lookups/CreateTransactionResult';
import { NotificationDuration } from '../../../../App/Models/NotificationDuration';
import InvestorInvestment from '../../../Models/InvestorInvestments/InvestorInvestment';
import IssuerInvestment from '../../../Models/IssuerInvestments/IssuerInvestment';
import { ConfigValues } from '../../../Models/Config/TenantConfig';
import { DateCycleType } from '../../../Models/Dividends/DateCycleType';
import { IDateCycleEditVM } from './DateCycleDropDown';

@NeoModel
export default class InvestmentComponentVM extends Views.ViewModelBase implements IDateCycleEditVM  {

    constructor(
        public investmentHost: IInvestmentHost,
        private investmentHostApiClient: IInvestmentHostApiClient,
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private onTransactionChange: () => void,
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        private issuerInvestmentApiClient = AppService.get(DomainTypes.ApiClients.IssuerInvestment),
    ) {

        super(taskRunner);

        if (investmentHost instanceof InvestorInvestment) {
            this.isInvestor = true;
            this.isLiquidityProvider = investmentHost.lookup.isLiquidityProvider;
            this.isExempt = investmentHost.lookup.isExempt;
            this.parentIsAuthorised = investmentHost.lookup.isInvestorAuthorised;
            this.parentIsRestricted = investmentHost.lookup.isInvestorRestricted;
        }
        if (investmentHost instanceof IssuerInvestment) {
            this.parentIsAuthorised = investmentHost.lookup.isIssuerAuthorised;
            this.parentIsRestricted = investmentHost.lookup.isIssuerRestricted;
        }
    }

    public readonly isInvestor: boolean = false;
    public readonly isExempt: boolean = false;
    public readonly isLiquidityProvider: boolean = false;
    public readonly parentIsAuthorised: boolean = false;
    public readonly parentIsRestricted: boolean = false;
    public config: ConfigValues | null = null;

    public get investment() {
        return this.investmentHost.investment;
    }

    public async initialise() {
        this.config = await this.dataCache.tenantConfig.getDataAsync();
    }

    /**
     * Rates
     */

    public addRate() {
        const newRate = this.investment.investmentRates.addNew();
        if ((this.investment.initialTransaction || this.investment.lookup.shareTransactions.length > 0) && this.investment.investmentRates.length === 1) {
            newRate.effectiveDate = this.investment.initialTransaction?.transactionDate ?? this.investment.lookup.shareTransactions[0].transactionDate;
        }
    }

    /**
     * Transactions
     */

    public newTransaction: ShareTransactionCommand | null = null;
    public newTransactionTask = AppService.get(Types.Neo.TaskRunner);

    public addTransaction() {
        if (this.investment.isNew) {
            this.investment.initialTransaction = new ShareTransactionCommand(this.investmentHost);
        } else {
            this.newTransaction = new ShareTransactionCommand(this.investmentHost);
        }
    }

    public async placeNotice(noticePeriodId: number) {

        const command = new ShareTransactionCommand(this.investmentHost);

        if (this.investmentHost.commonFields.productTypeId) {
            const productTypes = await this.dataCache.productTypes.getDataAsync();
            const productType = productTypes.find(c => c.productTypeId === this.investmentHost.commonFields.productTypeId);
            if (productType?.allowImmediateNotice) {
                command.noticePeriodDays = 0;
            }
        }

        if (command.noticePeriodDays === null) {
            const noticePeriods = await this.dataCache.noticePeriods.getDataAsync();
            command.noticePeriodDays = noticePeriods.find(c => c.noticePeriodId === noticePeriodId)?.days ?? null;
        }

        command.transactionType = TransactionType.Redemption;

        this.newTransaction = command;
    }

    public async onNoticeDateChanged(transaction: ShareTransactionCommand) {

        if (transaction.noticeDate && transaction.noticePeriodDays !== null) {
            let minDate = DateUtils.add(transaction.noticeDate, { days: transaction.noticePeriodDays });
            minDate = await this.newTransactionTask.waitFor(this.dataCache.calculateBusinessDayAsync(minDate));
            transaction.minDate = minDate;
            transaction.transactionDate = transaction.minDate;
            await this.onTransactionDateChanged(transaction);
        }
    }

    public async onTransactionDateChanged(transaction: ShareTransactionCommand) {

        if (transaction.hasValidTransactionDate) {
            if (this.investmentHost.issuerInvestmentId) {

                const utilisationData = await this.newTransactionTask.waitForData(this.issuerInvestmentApiClient.getUtilisation(UtilisationCriteria.getCriteriaObject({
                    balanceDate: transaction.transactionDate,
                    issuerInvestmentId: this.investmentHost.issuerInvestmentId
                })));

                transaction.utilisation = UtilisationLookup.fromJSObject<UtilisationLookup>(utilisationData);
            }
        }
    }

    public get createTransactionDisabledTooltipText() {
        if (this.parentIsRestricted) {
            return `${this.isInvestor ? "Investor" : "Issuer"} is restricted from trading`;
        } else {
            return `${this.isInvestor ? "Investor" : "Issuer"} not yet authorised`;
        }
    }

    public async saveTransaction() {
        this.taskRunner.run(async () => {
            if (this.newTransaction) {
                const response = await this.investmentHostApiClient.createShareTransaction(this.newTransaction.toJSObject());
                const insertResult = CreateTransactionResult.fromJSObject<CreateTransactionResult>(response.data);
                if (insertResult.shareTransactionLookup) {
                    this.investmentHost.investment.lookup.shareTransactions.push(insertResult.shareTransactionLookup);
                    this.onTransactionChange();
                }
                if (insertResult.pendingRequest) {
                    this.investmentHost.investment.lookup.pendingTransactionRequests.push(insertResult.pendingRequest);
                    this.notifications.addSuccess("Transaction", "Transaction request created successfully. Note: the remaining balance of the transaction will be fulfilled when shares become available.");
                } else {
                    this.notifications.addSuccess("Transaction", "Transaction created successfully.", NotificationDuration.Standard);
                }
                this.investment.isSuspended = true;
                this.investment.endDate = insertResult.newEndDate;
                this.investment.isSuspended = false;

                this.newTransaction = null;
                this.investment.lookup.resetAccruals();

                if (insertResult.unAuthoriseInvestment) {
                    this.investmentHost.authorisation.isAuthorised = false;
                    this.investmentHost.authorisation.isSelfDirty = false;
                }
            }
        });
    }

    public deleteTransaction(transaction: ShareTransactionLookup) {
        this.taskRunner.run(async () => {

            const result = await this.investmentHostApiClient.deleteShareTransaction({
                shareTransactionId: transaction.shareTransactionId,
                transactionRequestId: transaction.transactionRequestId
            });

            this.notifications.addSuccess("Transaction", "Transaction deleted successfully.", NotificationDuration.Standard);
            this.investment.lookup.shareTransactions.removeWithoutTracking(transaction);

            this.investment.lookup.resetAccruals();
            this.onTransactionChange();
        })
    }

    /**
     * Date cycles
     */

    public dateCycleIsDividendCycle = false;
    public dateCycleVM = this.registerViewModel(DateCyclesVM);

    public editDateCycle(dateCycleId: number | null, dateCycle: DateCycle | null, dateCycleType: DateCycleType) {

        if (dateCycleId === null) {
            dateCycle = new DateCycle();
            dateCycle.dateCycleName = "Custom";
            dateCycle.forSingleInvestment = true;
            dateCycle.dateCycleType = dateCycleType;
        }

        if (dateCycle) {
            this.dateCycleVM.editDateCycle(dateCycle, this.investment.lookup.hasDividends);
        } else {
            this.dateCycleVM.beginEditDateCycle(dateCycleId!, this.investment.lookup.hasDividends, this.investment.investmentId);
        }

        this.dateCycleIsDividendCycle = dateCycleType === DateCycleType.Dividends;

    }

    public async saveDateCycle() {

        if (this.dateCycleVM.editingDateCycle) {
            if (this.dateCycleVM.editingDateCycle.isNew) {

                if (this.dateCycleIsDividendCycle) {
                    this.investment.dividendCycle = this.dateCycleVM.editingDateCycle;
                    this.investment.dividendCycleId = 0;
                    this.investment.lookup.dividendCycleName = this.dateCycleVM.editingDateCycle.dateCycleName;
                } else {
                    this.investment.compoundingCycle = this.dateCycleVM.editingDateCycle;
                    this.investment.compoundingCycleId = 0;
                    this.investment.lookup.compoundingCycleName = this.dateCycleVM.editingDateCycle.dateCycleName;
                }

            } else {
                await this.dateCycleVM.saveDateCycle(this.investment.investmentId);
            }
        }
        this.dateCycleVM.editingDateCycle = null;
    }
}